import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./FullAbt.css";
import solarImg from "../../assets/FullAbt/AbtHero.png";
import partnersImg from "../../assets/FullAbt/Abt.png";
import CustomButton from "../Button/CustomButton";
import { useNavigate } from "react-router-dom";
import proj1 from "../../assets/FullAbt/img (1).png";
import proj2 from "../../assets/FullAbt/img (2).png";
import proj3 from "../../assets/FullAbt/img (3).png";
import proj4 from "../../assets/FullAbt/img (4).png";
import ctaImg from "../../assets/FullAbt/cta.png";
import shapesBg from "../../assets/FullAbt/AbtHero2.png"; // New BG image for the banner

const FullAbout = () => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  const navigate = useNavigate();


  const faqData = [
    {
      q: "What does Dazbog do?",
      a: "Dazbog supports renewable energy projects from start to finish. We provide advisory, technical support, financial structuring, and execution oversight for solar, hybrid, and emerging energy projects.",
    },
    {
      q: "Do you work only as an advisor, or also during execution?",
      a: "We work across the full project lifecycle. Along with advisory, we support EPC coordination, execution monitoring, and commissioning to ensure projects are completed smoothly.",
    },
    {
      q: "What types of renewable energy projects do you handle?",
      a: "We work mainly on utility-scale solar projects, solar + battery storage (BESS), hybrid renewable projects, and emerging energy solutions like green hydrogen.",
    },
    {
      q: "Who do you typically work with?",
      a: "We work with developers, investors, banks, EPC companies, and government or institutional clients across India.",
    },
    {
      q: "At what stage can we engage Dazbog?",
      a: "You can engage us at any stage—early project planning, financial structuring, technical due diligence, execution, or even post-commissioning support.",
    },
  ];

  const revealVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const zoomVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: "backOut" },
    },
  };

  return (
    <section className="full-abt-section">
      <div className="abt-hero-dark">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="container text-center"
        >
          <h1 className="abt-main-title mt-5">About Us</h1>
          <p className="abt-hero-lead">
            Dazbog works with developers, investors, and institutions to deliver
            renewable energy projects from start to finish. We support solar,
            hybrid, and emerging energy projects with practical advice, strong
            execution, and on-ground experience.
          </p>
        </motion.div>
      </div>

      <div className="abt-image-strip">
        <img src={solarImg} alt="Solar Panels" className="full-strip-img" />
      </div>

      <div className="container">
        <motion.div
          className="stats-row"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="stats-grid">
            <div className="stat-item">
              <h2 className="stat-number">15+</h2>
              <p className="stat-label">Years of Renewable Energy Experience</p>
            </div>
            <div className="stat-item">
              <h2 className="stat-number">$1B+</h2>
              <p className="stat-label">Projects & Transactions Advised</p>
            </div>
          </div>
          <div className="stats-desc-box">
            <p className="stats-desc-text">
              We support renewable energy projects with strong technical,
              financial, and execution expertise. Our experience spans solar,
              wind, hybrid, and emerging energy projects across India.
            </p>
          </div>
        </motion.div>

        <div className="partners-card-wrapper">
          <div className="partnerships-row">
            <motion.div
              className="partners-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="partners-title">Building strong partnerships</h2>
              <p className="partners-text">
                We work closely with developers, investors, and partners to
                deliver renewable energy projects successfully.
              </p>
              <CustomButton
                variant="green"
                size="md"
                text="Partner With Dazbog"
                onClick={() => navigate("/contact")}
              />
            </motion.div>
            <motion.div
              className="partners-image-container"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              <img
                src={partnersImg}
                alt="Partnerships"
                className="partners-img"
              />
              <div className="img-glass-overlay">
                <p>
                  We focus on long-term partnerships to deliver reliable and
                  bankable solar and clean energy projects.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* --- NEW SECTION: SHAPING THE FUTURE CLEAN ENERGY --- */}
      <div
        className="shaping-future-banner"
        style={{ backgroundImage: `url(${shapesBg})` }}
      >
        <div className="container text-center">
          <h2>Shaping the Future ☀️ Clean Energy</h2>
          <p>
            We support clean energy projects with practical advice and strong
            execution, <br /> helping build a reliable and sustainable energy
            future.
          </p>
        </div>
      </div>

      <div className="container">
        {/* --- RENEWABLE PROJECTS SECTION (Equal Image Sizes & Correct Alignment) --- */}
        <motion.div
          className="projects-section py-5"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={revealVariants}
        >
          <div className="text-center mb-5">
            <h2 className="section-title-dark">
              Our Renewable Energy <br /> Project Experience
            </h2>
            <p className="section-subtitle-muted">We support renewable energy projects across planning, advisory, and execution. Our work focuses on large-scale, bankable clean energy solutions.</p>
          </div>

          <div className="projects-grid-staggered">
            {/* Left Column (Starts Higher) */}
            <div className="proj-column left-col">
              <div className="proj-item">
                <div className="img-wrapper">
                  <img src={proj1} alt="Utility-Scale" />
                </div>
                <h4>Utility-Scale Solar Projects</h4>
                <p>
                  We advise and support large-scale solar power projects, from
                  early planning to execution and commissioning.
                </p>
              </div>
              <div className="proj-item">
                <div className="img-wrapper">
                  <img src={proj3} alt="Hybrid" />
                </div>
                <h4>Hybrid Renewable Projects (Solar + Wind)</h4>
                <p>
                  We support hybrid renewable projects that combine solar and
                  wind for better reliability.
                </p>
              </div>
            </div>

            {/* Right Column (Offset Lower) */}
            <div className="proj-column right-col">
              <div className="proj-item">
                <div className="img-wrapper">
                  <img src={proj2} alt="BESS" />
                </div>
                <h4>Solar + BESS (Battery Storage) Projects</h4>
                <p>
                  We provide technical and financial advisory for solar projects
                  combined with battery energy storage systems.
                </p>
              </div>
              <div className="proj-item">
                <div className="img-wrapper">
                  <img src={proj4} alt="Hydrogen" />
                </div>
                <h4>Emerging Energy & Green Hydrogen</h4>
                <p>
                  We work on emerging clean energy solutions, including green
                  hydrogen and future-ready energy technologies.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* --- FAQ & CTA SECTIONS --- */}
        <div className="faq-section py-5">
          <div className="row">
            <div className="col-lg-5">
              <h2 className="faq-main-title">
                Know More About <br /> Dazbog
              </h2>
              <p className="faq-main-subtitle">
                Answers to common questions about our role.
              </p>
            </div>
            <div className="col-lg-7">
              {faqData.map((faq, i) => (
                <div key={i} className="faq-item">
                  <div
                    className="faq-question"
                    onClick={() => setActiveFAQ(activeFAQ === i ? null : i)}
                  >
                    <span>{faq.q}</span>
                    <span className="faq-toggle">
                      {activeFAQ === i ? "−" : "+"}
                    </span>
                  </div>
                  <AnimatePresence>
                    {activeFAQ === i && (
                      <motion.div
                        className="faq-answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          className="final-cta-card"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={zoomVariants}
        >
          <div className="cta-content">
            <h2>
              Let’s Build Your <br /> Renewable Energy <br /> Project
            </h2>
            <CustomButton
              variant="white"
              size="md"
              text="Discuss Your Project"
              onClick={() => navigate("/contact")}
            />
          </div>
          <div className="cta-image-box">
            <img src={ctaImg} alt="Construction Site" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FullAbout;
